import React from "react";
import { Link } from "react-router-dom";
import { Linkedin, Instagram, Facebook, ArrowUpRight } from "lucide-react";
import { LogoMark } from "./Logo";
import { NAV_LINKS, SOCIALS, CONTACT_DETAILS } from "../config/siteConfig";

const FOOTER_LINKS = NAV_LINKS.filter((l) => l.label !== "Insights");

export const Footer = () => {
  return (
    <footer className="bg-[#111111] text-[#F7F7F5] pt-24 pb-10 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <h2 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-4xl">
          People. <span className="text-[#E03535]">Opportunity.</span> Progress.
        </h2>

        <div className="grid md:grid-cols-4 gap-12 mt-20 pt-14 border-t border-white/10">
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center gap-3 group" aria-label="Prospera Staffing home">
              <LogoMark size={38} />
              <span className="font-accent leading-none text-xl tracking-wide">
                <span className="text-[#C62828]" style={{ fontWeight: 700 }}>PROSPERA</span>{" "}
                <span className="text-[#F7F7F5]" style={{ fontWeight: 500 }}>STAFFING</span>
              </span>
            </Link>
            <p className="font-body text-white/55 text-sm leading-relaxed mt-5 max-w-sm">
              Talent and workforce solutions connecting exceptional professionals with ambitious
              organizations across Dubai and the GCC.
            </p>
            <div className="flex gap-3 mt-7">
              {[
                { Icon: Linkedin, href: SOCIALS.linkedin, name: "LinkedIn" },
                { Icon: Instagram, href: SOCIALS.instagram, name: "Instagram" },
                { Icon: Facebook, href: SOCIALS.facebook, name: "Facebook" },
              ].map(({ Icon, href, name }) => (
                <a
                  key={name}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={name}
                  data-testid={`social-${name.toLowerCase()}`}
                  className="w-11 h-11 rounded-sm border border-white/15 flex items-center justify-center hover:bg-[#C62828] hover:border-[#C62828] transition-colors duration-300"
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <p className="overline text-[10px] text-white/40 mb-5">Navigate</p>
            <ul className="space-y-3">
              {FOOTER_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="font-body text-sm text-white/60 hover:text-white transition-colors inline-flex items-center gap-1 group"
                  >
                    {l.label}
                    <ArrowUpRight size={13} className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="overline text-[10px] text-white/40 mb-5">Contact</p>
            <ul className="space-y-3 font-body text-sm text-white/60">
              <li>{CONTACT_DETAILS.location}</li>
              <li><a href={`tel:${CONTACT_DETAILS.phone.replace(/\s+/g, "")}`} className="hover:text-white transition-colors">{CONTACT_DETAILS.phone}</a></li>
              <li className="text-white/40 text-xs">{CONTACT_DETAILS.hours}</li>
              <li><a href={`mailto:${CONTACT_DETAILS.email}`} className="hover:text-white transition-colors">{CONTACT_DETAILS.email}</a></li>
            </ul>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mt-16 pt-8 border-t border-white/10">
          <p className="font-body text-xs text-white/40">© 2026 Prospera Staffing. All Rights Reserved.</p>
          <div className="flex gap-6">
            <a href="#" data-testid="footer-privacy" className="font-body text-xs text-white/40 hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" data-testid="footer-terms" className="font-body text-xs text-white/40 hover:text-white transition-colors">Terms of Use</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
